import React, { useContext, useEffect, useState } from "react";
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import Dialog from "@mui/material/Dialog";
import { TextField, Button, Grid, Box } from "@mui/material";
import { CardContext } from "./Perfil/Plan/plan";
import visaLogo from "./logos/VISA.png";
import mastercardLogo from "./logos/MASTERCARD.png";
import unionPayLogo from "./logos/UNIONPAY.png";
import Turnstile, { useTurnstile } from "react-turnstile";
import ReCAPTCHA from "react-google-recaptcha";
import "./paymentPage.css";
import "./captcha.css";

const PaymentForms = ({ open, onClose, handlePayment }) => {
  const { cardInfo, setCardInfo } = useContext(CardContext);
  const [isValid, setIsValid] = useState(false);
  const [isCardValid, setIsCardValid] = useState(false);
  const turnstile = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const [errors, setErrors] = useState({
    number: false,
    name: false,
    expiry: false,
    cvc: false,
  });

  const handleInputChange = (e) => {
    setCardInfo({
      ...cardInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleFocusChange = (e) => {
    setCardInfo({
      ...cardInfo,
      focus: e.target.name,
    });
  };

  const handleClose = () => {
    setCardInfo({
      number: "",
      name: "",
      expiry: "",
      cvc: "",
      focus: "",
    });
    setTurnstileToken(null);
    setIsCardValid(false);
    setIsValid(false);
    setWasSubmitted(false);
    setErrors({
      number: false,
      name: false,
      expiry: false,
      cvc: false,
    });
    onClose();
  };

  useEffect(() => {
    if (isCardValid && turnstileToken !== null) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [turnstileToken, isCardValid]);

  const getFormattedExpiry = (val) => {
    let value = val.replace(/\D/g, ""); // Solo números
    if (value.length > 4) value = value.slice(0, 4);
    if (value.length >= 3) {
      const month = value.slice(0, 2);
      let year = value.slice(2);
      if (parseInt(month) > 12) value = "12" + year;
      else value = month + "/" + year;
    }
    return value;
  };

  const validateFields = () => {
    const newErrors = {
      number: !(cardInfo.number.length === 16),
      name: cardInfo.name.trim().length === 0,
      expiry: !(cardInfo.expiry.length === 4),
      cvc: !(cardInfo.cvc.length >= 3),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  const handleClickPay = () => {
    setWasSubmitted(true);
    if (validateFields() && isCardValid && turnstileToken) {
      const expiryAPIFormat = `${cardInfo.expiry.slice(
        2
      )}${cardInfo.expiry.slice(0, 2)}`;
      handlePayment({ ...cardInfo, expiry: expiryAPIFormat });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} p={2}>
      <Box p={3}>
        <div className="card">
          <div className="card-body">
            <Cards
              number={cardInfo.number}
              name={cardInfo.name}
              expiry={cardInfo.expiry}
              cvc={cardInfo.cvc}
              focused={cardInfo.focus}
              callback={({ issuer }, isValid) => setIsCardValid(isValid)}
            />

            <form>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Número de la tarjeta"
                    name="number"
                    id="number"
                    inputProps={{ maxLength: 16 }}
                    onChange={handleInputChange}
                    onFocus={handleFocusChange}
                    variant="outlined"
                    error={wasSubmitted && errors.number}
                    helperText={
                      wasSubmitted && errors.number
                        ? "Número inválido (debe tener 16 dígitos)"
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="name"
                    id="name"
                    inputProps={{ maxLength: 30 }}
                    onChange={handleInputChange}
                    onFocus={handleFocusChange}
                    variant="outlined"
                    error={wasSubmitted && errors.name}
                    helperText={
                      wasSubmitted && errors.name ? "Nombre requerido" : ""
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha de expiración MM/YY"
                    name="expiry"
                    id="expiry"
                    value={getFormattedExpiry(cardInfo.expiry)}
                    inputProps={{ maxLength: 5 }}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // Solo números
                      setCardInfo({ ...cardInfo, expiry: value });
                    }}
                    onFocus={handleFocusChange}
                    variant="outlined"
                    error={wasSubmitted && errors.expiry}
                    helperText={
                      wasSubmitted && errors.expiry
                        ? "Formato incorrecto o incompleto (MMYY)"
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="CVC"
                    name="cvc"
                    id="cvc"
                    inputProps={{ maxLength: 4 }}
                    onChange={handleInputChange}
                    onFocus={handleFocusChange}
                    variant="outlined"
                    error={wasSubmitted && errors.cvc}
                    helperText={
                      wasSubmitted && errors.cvc ? "CVC inválido" : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} className="payment-logos">
                  <ReCAPTCHA
                    sitekey="6LensfIqAAAAAMiIfzavVXoKVS-aXIXxUiVL-5PG"
                    onChange={(value) => setTurnstileToken(value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    disabled={!turnstileToken}
                    onClick={handleClickPay}
                  >
                    Pagar
                  </Button>
                </Grid>
                <Grid item xs={12} className="payment-logos">
                  Aceptamos a través de SERFINSA:
                  <img src={visaLogo} alt="visa" width="50" />
                  <img src={mastercardLogo} alt="mastercard" width="50" />
                  <img src={unionPayLogo} alt="unionpay" width="50" />
                </Grid>
              </Grid>
            </form>
          </div>
        </div>
      </Box>
    </Dialog>
  );
};

export default PaymentForms;
